import { IconButton, Tab, Tabs } from '@mui/material';
import AppReminder from 'components/AppReminder';
import Header from 'components/Header/Header';
import HeaderIconLogo from 'components/Header/header-items/HeaderIconLogo';
import InfiniteScroll from 'components/InfiniteScroll/InfiniteScroll';
import BlockImage from 'components/layout/BlockImages/BlockImage/BlockImage';
import BlockImages from 'components/layout/BlockImages/BlockImages';
import LikesModal from 'components/LikesModal';
import Loader from 'components/Loader/Loader';
import MyShopTabs, { MyShopTabName } from 'components/my/shop/MyShopTabs/MyShopTabs';
import NotFound from 'components/NotFound/NotFound';
import SellButton from 'components/SellButton/SellButton';
import ShopUserProfileFeedback from 'components/Shop/ShopUserProfile/Feedback/ShopUserProfileFeedback';
import { ShopFeedbackTab } from 'components/ShopFeedbackTab/ShopFeedbackTab';
import withAuth from 'components/with-auth/with-auth';
import { PRODUCT } from 'config/fe-routes';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import ShareIcon from 'public/icons/icn-ios-share.svg';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import ProductService from 'services/product.service';
import ShopService from 'services/shop.service';
import { setMyShopAvailableProducts, setMyShopOtherProducts } from 'store/actions/my-shop-page.actions';
import { selectAvailableProducts, selectAvailableTotal, selectOtherStatusesProducts, selectOtherTotal } from 'store/selectors/my-shop-page';
import { ProductType } from 'types/product.types';
import { ShopType } from 'types/shop.types';
import { UserType } from 'types/user.types';
import ImageUrlResizer from 'utils/image-url-resizer';
import { notification } from 'utils/snackbar';
import { getHostname } from 'utils/url';

import { useLanguage } from '../../hooks/useLanguage';

const productService = new ProductService();
const shopService = new ShopService();

type MyShopProps = {
  user: UserType;
  shop: ShopType;
  reloadShop?: () => void;
  availableProducts: ProductType[];
  availableTotal: number | null;
  otherStatusesProducts: ProductType[];
  otherTotal: number | null;
};

const mapStateToProps = (state) => ({
  user: state.userData && state.userData.user,
  availableProducts: selectAvailableProducts(state),
  availableTotal: selectAvailableTotal(state),
  otherStatusesProducts: selectOtherStatusesProducts(state),
  otherTotal: selectOtherTotal(state),
});

const MyShopFeedbackPage = (props: MyShopProps) => {
  const { user, shop, reloadShop, availableProducts, availableTotal, otherStatusesProducts, otherTotal } = props;

  const { t } = useTranslation();
  const { locale } = useLanguage();
  const { query, isReady } = useRouter();

  const [isLikesModalOpen, setLikesModalOpen] = useState(false);
  const [likeCount, setLikeCount] = useState(null);
  const [activeTab, setActiveTab] = useState<'items' | 'reviews'>('items');
  // Automatic reviews already reported as shown in this shop visit. Owned here because the Reviews tab unmounts on Items.
  const trackedAutomaticReviewIds = useMemo(() => new Set<number>(), [user?.shop?.id]);
  const [headerHeight, setHeaderHeight] = useState(60);

  const tabSwitcherRef = useRef<HTMLDivElement>(null);
  const loadingAvailableRef = useRef(false);
  const loadingOtherRef = useRef(false);

  // Derived values - null total means we haven't fetched yet, so hasMore is true
  const hasMoreAvailableProducts = availableTotal === null || availableProducts.length < availableTotal;
  const hasMoreOtherStatusesProducts = otherTotal === null || otherStatusesProducts.length < otherTotal;

  // Track if we've already loaded the initial page of "other status" products from the backend.
  const [hasLoadedInitialOtherStatuses, setHasLoadedInitialOtherStatuses] = useState(false);

  const ITEMS_PER_ROW = 4;
  const PRODUCTS_PER_PAGE = ITEMS_PER_ROW * 10;

  useEffect(() => {
    const headerEl = document.getElementById('global-header');

    if (!headerEl) {
      return () => {
        // Do nothing.
      };
    }

    const update = () => setHeaderHeight(headerEl.offsetHeight);

    update();
    window.addEventListener('resize', update);

    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (isReady && query.tab === 'reviews') {
      setActiveTab('reviews');
    }
  }, [isReady]);

  // Load next set of published products
  const loadMoreAvailableProducts = async (page: number) => {
    if (loadingAvailableRef.current) {
      return;
    }
    loadingAvailableRef.current = true;

    try {
      const { list: nextProducts, total } = await productService.getProducts({
        status: 'published',
        shopId: user.shop?.id,
        offset: PRODUCTS_PER_PAGE * page,
        limit: PRODUCTS_PER_PAGE,
      });

      setMyShopAvailableProducts(nextProducts || [], total);
    } catch (e) {
      // Do nothing
    } finally {
      loadingAvailableRef.current = false;
    }
  };

  // Load next set of sold products
  const loadMoreOtherStatusesProducts = async (page: number) => {
    if (loadingOtherRef.current) {
      return;
    }
    loadingOtherRef.current = true;

    try {
      // Mark that we've started loading "other status" products from the backend.
      if (!hasLoadedInitialOtherStatuses) {
        setHasLoadedInitialOtherStatuses(true);
      }

      const { list: nextProducts, total } = await productService.getProducts({
        status: ['reserved', 'sold', 'not-visible'],
        shopId: user.shop?.id,
        offset: PRODUCTS_PER_PAGE * page,
        limit: PRODUCTS_PER_PAGE,
      });

      setMyShopOtherProducts(nextProducts || [], total);
    } catch (e) {
      // Do nothing
    } finally {
      loadingOtherRef.current = false;
    }
  };

  // When all published products are loaded, ensure we load the first page of "other status"
  // products at least once.
  useEffect(() => {
    if (!hasMoreAvailableProducts && !hasLoadedInitialOtherStatuses) {
      loadMoreOtherStatusesProducts(0);
    }
  }, [hasMoreAvailableProducts, hasLoadedInitialOtherStatuses]);

  const getLikeCount = async () => {
    try {
      const myShop = await shopService.getShop(user?.shop?.activeSlug);

      setLikeCount(myShop?.owner?.likeCount);
    } catch (e) {
      // Do nothing
    }
  };

  useEffect(() => {
    getLikeCount();
  }, []);

  const onClickShare = () => {
    navigator.clipboard.writeText(window.location.href);
    notification.showSuccessNotification(t('common.link-copied'));
  };

  const mergedShop = {
    ...user?.shop,
    feedbacks: shop?.feedbacks,
    userFeedbacks: shop?.userFeedbacks,
    systemFeedbacks: shop?.systemFeedbacks,
    description: shop?.description ?? user?.shop?.description,
  };

  return (
    <>
      <AppReminder />

      <NextSeo
        openGraph={{
          title: `${getHostname()}: ${user.shop.name}`,
          description: user.shop?.description,
          images: [
            {
              url: availableProducts?.[0]?.images?.[0]?.gallery || otherStatusesProducts?.[0]?.images?.[0]?.gallery,
              width: 600,
              height: 600,
              alt: user.shop?.name,
            },
          ],
          type: 'profile',
          locale,
          profile: {
            firstName: user.shop?.owner?.first_name,
            lastName: user.shop?.owner?.last_name,
            username: user.shop?.owner?.shop?.activeSlug,
          },
        }}
      />

      <Header>
        <HeaderIconLogo icon="home" showLogo={false}>
          @{user?.shop?.activeSlug}
        </HeaderIconLogo>
      </Header>

      <div className="shop-wrapper mb-14">
        <MyShopTabs value={MyShopTabName.MY_SHOP} />

        <div className="shop-page feedback-enabled">
          <div className="shop-tab-switcher-actions" style={{ top: headerHeight }}>
            <div className="shop-tab-switcher-actions__buttons">
              <IconButton aria-label="share" aria-controls="share-menu" aria-haspopup="true" onClick={onClickShare}>
                <ShareIcon />
              </IconButton>
            </div>
          </div>

          <ShopUserProfileFeedback
            shop={mergedShop as ShopType}
            currentUser={user}
            profileImage={user?.picture}
            orderCount={shop?.completedOrdersCount}
            likeCount={likeCount}
            lastActive={shop?.owner?.lastActive}
            likeShop={() => setLikesModalOpen(true)}
            refreshParentDataFn={reloadShop}
            onRatingClick={() => {
              setActiveTab('reviews');
              setTimeout(() => {
                tabSwitcherRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 500);
            }}
          />

          <div className="shop-tab-switcher" ref={tabSwitcherRef} style={{ top: headerHeight }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              TabIndicatorProps={{
                style: {
                  height: '40px',
                  borderRadius: '21px',
                  backgroundColor: '#ffffff',
                  bottom: 0,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                },
              }}
            >
              <Tab value="items" label={t('components.page-shop.tab-items')} disableRipple />
              <Tab value="reviews" label={t('components.page-shop.tab-reviews')} disableRipple />
            </Tabs>
          </div>

          {activeTab === 'items' && (
            <>
              <BlockImages>
                {/* available products */}
                <InfiniteScroll
                  dataLength={availableProducts.length}
                  batchSize={PRODUCTS_PER_PAGE}
                  loadMore={(page) => loadMoreAvailableProducts(page)}
                  hasMore={hasMoreAvailableProducts}
                  loader={<Loader />}
                  resetDependencies={[user?.shop.id]}
                >
                  {availableProducts.map((product, index) => {
                    const productImage = product?.images?.[0];

                    return (
                      <BlockImage
                        key={'shop-available-product-key-' + product?.id}
                        isCharity={product?.tags?.includes('Charity') || product?.isCharityProduct}
                        src={ImageUrlResizer.resize(productImage.original, 600, true)}
                        alt={productImage?.title}
                        price={product.price}
                        priceDrop={product.priceDrop}
                        productSlug={product.slug}
                        status={product.status}
                        details={product.size}
                        brand={product.brand}
                        tagged={product.tagged}
                        md={3}
                        globalLoaderEnabled={true}
                        productId={product.id}
                        likeCount={product.likeCount}
                        isOwner={true}
                        {...PRODUCT(product?.shop?.activeSlug, product.slug, undefined, index)}
                      />
                    );
                  })}
                </InfiniteScroll>

                {/* sold and other statuses products - load them when no more published products available */}
                {!hasMoreAvailableProducts && (
                  <InfiniteScroll
                    dataLength={otherStatusesProducts.length}
                    batchSize={PRODUCTS_PER_PAGE}
                    loadMore={(page) => loadMoreOtherStatusesProducts(page)}
                    hasMore={hasMoreOtherStatusesProducts}
                    loader={<Loader />}
                    resetDependencies={[user?.shop.id]}
                  >
                    {otherStatusesProducts.map((product, index) => {
                      const productImage = product?.images?.[0];

                      return (
                        <BlockImage
                          key={'shop-sold-product-key-' + product?.id}
                          isCharity={product?.tags?.includes('Charity') || product?.isCharityProduct}
                          src={ImageUrlResizer.resize(productImage.original, 600, true)}
                          alt={productImage?.title}
                          price={product.price}
                          status={product.status}
                          details={product.size}
                          brand={product.brand}
                          tagged={product.tagged}
                          md={3}
                          globalLoaderEnabled={true}
                          productId={product.id}
                          likeCount={product.likeCount}
                          isOwner={true}
                          {...PRODUCT(product?.shop?.activeSlug, product.slug, undefined, index)}
                        />
                      );
                    })}
                  </InfiniteScroll>
                )}
              </BlockImages>

              {!hasMoreAvailableProducts && !hasMoreOtherStatusesProducts && !availableProducts.length && !otherStatusesProducts.length && (
                <div className="mt-6 text-soft text-align-center">
                  <NotFound target="products" customMessage="pages.sinupood.not-found" title="" />
                </div>
              )}
            </>
          )}

          {activeTab === 'reviews' && (
            <ShopFeedbackTab
              shop={mergedShop as ShopType}
              isOpen={activeTab === 'reviews'}
              setOpen={() => setActiveTab('items')}
              feedbackReceiver={{
                ...mergedShop.owner,
                picture: mergedShop?.owner?.picture,
                shop: mergedShop as ShopType,
              }}
              refresh={reloadShop}
              trackedAutomaticReviewIds={trackedAutomaticReviewIds}
            />
          )}

          {/* LIKES MODAL */}
          <LikesModal
            isOpen={isLikesModalOpen}
            isLiked={false}
            isSeller={true}
            likeCount={likeCount}
            targetId={user.id}
            onClose={() => setLikesModalOpen(false)}
            productOrShopSlug={shop?.activeSlug}
          />
        </div>
      </div>

      {/* fixed sell button in the bottom */}
      <SellButton />
    </>
  );
};

export default connect(mapStateToProps)(withAuth(MyShopFeedbackPage));
